import React, { useEffect, useState } from 'react';
import api from '../../../services/api';
import { Row, Col, Card, Statistic, Table, Tag, Select, Alert, Button, Space, Tabs } from 'antd';
import { Link } from 'react-router-dom';
import { MessageOutlined, EyeOutlined, DollarOutlined } from '@ant-design/icons';
import { getStatusColor } from '../../../utils/ui';
import VendorScheduleEditor from '../../PaymentSchedule/VendorScheduleEditor';

function VendorDashboard() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [stats, setStats] = useState(null);
    const [bookings, setBookings] = useState([]);
    const [leads, setLeads] = useState([]);
    const [updatingId, setUpdatingId] = useState(null);
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');

    const statusOptions = [
        'pending',
        'confirmed',
        'preparing',
        'ready',
        'delivered',
        'cancelled',
    ];

    const loadData = async () => {
        try {
            const [statsRes, bookingsRes, leadsRes] = await Promise.all([
                api.getBookingStats(),
                api.getBookings({ per_page: 10 }),
                api.getLeads({ per_page: 10 }),
            ]);
            setStats(statsRes.data);
            setBookings(bookingsRes.data?.data || bookingsRes.data || []);
            setLeads(leadsRes.data?.data || leadsRes.data || []);
        } catch (e) {
            setError('Failed to load vendor data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateStatus = async (bookingId, status) => {
        try {
            setUpdatingId(bookingId);
            await api.updateBookingStatus(bookingId, status);
            await loadData();
        } catch (e) {
            setError('Failed to update status');
        } finally {
            setUpdatingId(null);
        }
    };

    const bookingColumns = [
        { title: 'Number', dataIndex: 'booking_number', key: 'number' },
        { title: 'Customer', dataIndex: ['user', 'name'], key: 'customer' },
        { title: 'Event', key: 'event', render: (_, b) => `${b.event_date} ${b.event_time} • Guests: ${b.guest_count}` },
        { title: 'Amount', dataIndex: 'total_amount', key: 'amount', render: (v) => `₹${v}` },
        {
            title: 'Status', key: 'status', render: (_, b) => (
                <Tag color={getStatusColor(b.status)}>{b.status}</Tag>
            )
        },
        {
            title: 'Update', key: 'update', render: (_, b) => (
                <Select
                    size="small"
                    value={b.status}
                    onChange={(v) => updateStatus(b.id, v)}
                    loading={updatingId === b.id}
                    options={statusOptions.map(s => ({ label: s, value: s }))}
                    style={{ minWidth: 140 }}
                />
            )
        },
        {
            title: 'Actions', key: 'actions', render: (_, b) => (
                <Space>
                    <Button
                        size="small"
                        icon={<DollarOutlined />}
                        onClick={() => {
                            setSelectedBooking(b);
                            setActiveTab('schedule');
                        }}
                    >
                        Schedule
                    </Button>
                    <Link to={`/chat?booking=${b.id}`}>
                        <Button size="small" icon={<MessageOutlined />}>Chat</Button>
                    </Link>
                </Space>
            )
        },
    ];

    const leadColumns = [
        { title: 'Customer', dataIndex: ['user', 'name'], key: 'customer' },
        { title: 'Event Type', dataIndex: 'event_type', key: 'event_type' },
        { title: 'Date & Time', key: 'datetime', render: (_, l) => `${l.event_date} ${l.event_time}` },
        { title: 'Guests', dataIndex: 'guest_count', key: 'guests' },
        { title: 'Venue', dataIndex: 'venue', key: 'venue', ellipsis: true },
        {
            title: 'Status', key: 'status', render: (_, l) => (
                <Tag color={getStatusColor(l.status)}>{l.status}</Tag>
            )
        },
        {
            title: 'Actions', key: 'actions', render: (_, l) => (
                <Space>
                    <Link to={`/vendor/leads`}>
                        <Button size="small" icon={<EyeOutlined />}>View</Button>
                    </Link>
                    <Link to={`/chat?lead=${l.id}`}>
                        <Button size="small" icon={<MessageOutlined />} type="primary">Chat</Button>
                    </Link>
                </Space>
            )
        },
    ];

    if (loading) {
        return (
            <div className="min-h-[50vh] flex items-center justify-center">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-500"></div>
            </div>
        );
    }

    const tabItems = [
        {
            key: 'overview',
            label: 'Overview',
            children: (
                <div>
                    {stats && (
                        <Row gutter={[16,16]} className="mb-8">
                            <Col xs={24} md={6}>
                                <Card>
                                    <Statistic title="Total Bookings" value={stats.total_bookings} />
                                </Card>
                            </Col>
                            <Col xs={24} md={6}>
                                <Card>
                                    <Statistic title="Confirmed" value={stats.confirmed_bookings} />
                                </Card>
                            </Col>
                            <Col xs={24} md={6}>
                                <Card>
                                    <Statistic title="Total Leads" value={leads.length} />
                                </Card>
                            </Col>
                            <Col xs={24} md={6}>
                                <Card>
                                    <Statistic title="Revenue" prefix="₹" value={stats.total_revenue} precision={0} />
                                </Card>
                            </Col>
                        </Row>
                    )}

                    <Row gutter={[16, 16]}>
                        <Col xs={24} lg={12}>
                            <Card>
                                <h2 className="text-xl font-semibold mb-3">Recent Bookings</h2>
                                <Table rowKey="id" columns={bookingColumns} dataSource={bookings} pagination={false} scroll={{ x: true }} />
                            </Card>
                        </Col>
                        <Col xs={24} lg={12}>
                            <Card>
                                <div className="flex items-center justify-between mb-3">
                                    <h2 className="text-xl font-semibold">Recent Leads</h2>
                                    <Link to="/vendor/leads">
                                        <Button type="primary" size="small">View All</Button>
                                    </Link>
                                </div>
                                <Table rowKey="id" columns={leadColumns} dataSource={leads} pagination={false} scroll={{ x: true }} />
                            </Card>
                        </Col>
                    </Row>
                </div>
            ),
        },
        {
            key: 'schedule',
            label: 'Payment Schedule',
            children: selectedBooking ? (
                <VendorScheduleEditor
                    bookingId={selectedBooking.id}
                    onScheduleUpdated={() => {
                        message.success('Payment schedule updated');
                        loadData();
                    }}
                />
            ) : (
                <div className="text-center py-8">
                    <p>Select a booking to manage its payment schedule</p>
                </div>
            ),
        },
    ];

    return (
        <div className="max-w-7xl mx-auto p-6">
            <h1 className="text-2xl font-bold mb-6">Vendor Dashboard</h1>
            {error && (<Alert type="error" message={error} className="mb-4" />)}

            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={tabItems}
            />
        </div>
    );
}

export default VendorDashboard;
